import React, { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

type Language = 'bg' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (bg: string, en: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const getLanguageFromParams = (params: URLSearchParams): Language => {
  return params.get('lang') === 'en' ? 'en' : 'bg';
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const language = getLanguageFromParams(searchParams);

  const setLanguage = useCallback(
    (lang: Language) => {
      const nextParams = new URLSearchParams(searchParams);

      if (lang === 'bg') {
        nextParams.delete('lang');
      } else {
        nextParams.set('lang', lang);
      }

      if (nextParams.toString() === searchParams.toString()) {
        return;
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t: (bg: string, en: string) => (language === 'bg' ? bg : en),
    }),
    [language, setLanguage]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
